import { ActionMenu, MenuItem } from '@/common/components/ActionMenu';
import { Planner, Todo } from '@/common/types/databaseTypes';
import { useDeletePlanner } from '@/features/planner/services/usePlannerQueries';
import { useTodoDrawerStore } from '@/stores/todoDrawerStore';
import { useTodoModalStore } from '@/stores/todoModalStore';
import { notifications } from '@mantine/notifications';
import {
    IconBrowserX,
    IconRotate2,
    IconSquareForbid2,
    IconTrash,
} from '@tabler/icons-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCancelTodo } from '../hooks/useTodoActions';
import { useDeleteTodo } from '../services/useTodoQueries';

interface TodoActionMenuProps {
    todo?: Partial<Todo>;
    planner?: Partial<Planner>;
    onClose?: () => void;
    onMenuChange?: (opened: boolean) => void; // 메뉴 열었을 때 추가로 수행할 작업(ex. 하이라이팅)이 있을 때 사용
    menuIcon?: React.ReactNode;
    variant?: 'subtle' | 'default';
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    position?:
        | 'right'
        | 'left'
        | 'bottom'
        | 'top'
        | 'bottom-end'
        | 'bottom-start'
        | 'top-end'
        | 'top-start'
        | 'left-end'
        | 'left-start'
        | 'right-end'
        | 'right-start';
    withinPortal?: boolean; // default true
}

export const TodoActionMenu: React.FC<TodoActionMenuProps> = ({
    todo,
    planner,
    onClose,
    onMenuChange,
    menuIcon,
    variant = 'default',
    size = 'sm',
    radius = 'sm',
    position = 'bottom-end',
    withinPortal = true,
}) => {
    const { t } = useTranslation(['todo', 'common']);
    const { updateTodoCancelStatus } = useCancelTodo();
    const deletePlanner = useDeletePlanner();
    const deleteTodo = useDeleteTodo();

    const { isDrawerOpen, closeDrawer, selectedDate } = useTodoDrawerStore();
    const { openModal: openConfirmModal } = useTodoModalStore();

    const handleCancelTodo = async () => {
        await updateTodoCancelStatus({
            todoId: todo?.id ?? '',
            updateToCancelled: todo?.cancelled_date === null,
            ownerId: todo?.user_id ?? '',
        });

        onClose?.();
    };

    const handleRemovePlanner = async () => {
        const loadingNotificationId = notifications.show({
            title: t(
                'component.todo_action_menu.notifications.removing_plan_title'
            ),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await deletePlanner.mutateAsync({
                plannerId: planner?.id ?? '',
            });

            notifications.update({
                id: loadingNotificationId,
                title: t(
                    'component.todo_action_menu.notifications.removed_plan_title'
                ),
                message: t(
                    'component.todo_action_menu.notifications.removed_plan_message'
                ),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });
        } catch (error) {
            console.error('Failed to remove planner', error);

            notifications.update({
                id: loadingNotificationId,
                title: t(
                    'component.todo_action_menu.notifications.remove_plan_failed_title'
                ),
                message: t(
                    'component.todo_action_menu.notifications.remove_plan_failed_message'
                ),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            onClose?.();
        }
    };

    const handleDeleteTodo = async () => {
        const loadingNotificationId = notifications.show({
            title: t(
                'component.todo_action_menu.notifications.deleting_todo_title'
            ),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await deleteTodo.mutateAsync({
                todoId: todo?.id ?? '',
            });

            notifications.update({
                id: loadingNotificationId,
                title: t(
                    'component.todo_action_menu.notifications.deleted_todo_title'
                ),
                message: t(
                    'component.todo_action_menu.notifications.deleted_todo_message'
                ),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });
        } catch (error) {
            console.error('Failed to delete todo', error);

            notifications.update({
                id: loadingNotificationId,
                title: t(
                    'component.todo_action_menu.notifications.delete_todo_failed_title'
                ),
                message: t(
                    'component.todo_action_menu.notifications.delete_todo_failed_message'
                ),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            onClose?.();
        }
    };

    // 메뉴 아이템 구성
    const menuItems: MenuItem[] = [];

    // 1. 할 일 취소/재개 옵션 (todo가 있는 경우)
    if (todo?.id) {
        menuItems.push({
            id: 'cancel-todo',
            label:
                todo.cancelled_date !== null
                    ? t('component.todo_action_menu.resume')
                    : t('notification.cancel_action'),
            icon:
                todo.cancelled_date !== null ? (
                    <IconRotate2 size={14} color="gray" />
                ) : (
                    <IconSquareForbid2 size={14} color="gray" />
                ),
            onClick: () => {
                if (todo.cancelled_date !== null) {
                    void handleCancelTodo();
                    return;
                }

                // '그만하기'를 선택했을 때 - 모달 띄우기
                openConfirmModal({
                    modalMode: 'cancel',
                    targetDate:
                        planner?.planner_date ?? selectedDate?.toISOString(),
                    canRemovePlanner: planner?.id != null, // planner가 있는 경우에만 취소 가능, null, undefined, false, 0, '' 등은 false로 평가
                    handleCancelTodo,
                    handleRemovePlanner,
                    handleDeleteTodo,
                });
            },
        });
    }

    // 2. 계획 취소 옵션 (planner가 있는 경우)
    if (planner?.id) {
        menuItems.push({
            id: 'remove-planner',
            label: t('component.todo_action_menu.remove_plan'),
            icon: <IconBrowserX size={14} color="gray" />,
            onClick: () => {
                openConfirmModal({
                    modalMode: 'removePlanner',
                    targetDate:
                        planner?.planner_date ?? selectedDate?.toISOString(),
                    canRemovePlanner: true,
                    handleCancelTodo,
                    handleRemovePlanner,
                    handleDeleteTodo,
                });
                onClose?.();
            },
        });
    }

    // 3. 전체 할 일 삭제 옵션 (항상 표시)
    if (todo) {
        menuItems.push({
            id: 'delete-todo',
            label: t('component.todo_action_menu.delete_todo'),
            color: 'red',
            icon: <IconTrash size={14} />,
            onClick: () => {
                if (isDrawerOpen) {
                    closeDrawer();
                }

                openConfirmModal({
                    modalMode: 'delete',
                    targetDate:
                        planner?.planner_date ?? selectedDate?.toISOString(),
                    canRemovePlanner: planner?.id != null, // planner가 있는 경우에만 취소 가능, null, undefined, false, 0, '' 등은 false로 평가
                    handleCancelTodo,
                    handleRemovePlanner,
                    handleDeleteTodo,
                });
                onClose?.();
            },
        });
    }

    return (
        <ActionMenu
            withinPortal={withinPortal}
            position={position}
            onMenuChange={onMenuChange}
            radius={radius}
            actionIcon={menuIcon}
            size={size}
            variant={variant}
            items={menuItems}
        />
    );
};
